import { ApiRequest, patchItem } from "utils/ApiUtils";
import buildQuery from 'odata-query';
import ODataUtil from 'utils/ODataUtil';
import { Inspection, InspectionReport, InspectionView } from 'classes';

const endpoint = "/InspectionViewWeb";
const endpointExport = "/InspectionView";
const endpointInspection = "/Inspection";

// $count over the INSPECTIONVIEW SQL view is roughly as expensive as the data query
// itself (it forces a full evaluation of the view's computed columns). The total only
// changes when the filter changes — not when paging, sorting, or changing page size.
// So we count once per filter signature and reuse it across those interactions.
const _inspectionCountCache = new Map();


function reduceInspTrackDate(IHWI) {
  const trackingDates = IHWI.reduce((a, b) => [...a, ...b.InspectorTracking], []).filter(r => r.OccurenceDate).map(r => r.OccurenceDate).sort((a, b) => new Date(a) < new Date(b));
  return trackingDates.length > 0 ? trackingDates[0] : null;
}

function convert_inspections_to_table_schema({ "value": items, "@odata.count": count }) {
  console.log(items)
  const data = items.map(r => {
    const inspection = new Inspection(r);
    inspection.Name_SystemUser_Rassignment = r.Rassignment && r.Rassignment.SystemUser.Name;
    inspection.Name_SystemUser_Qcresource = r.Qcresource && r.Qcresource.SystemUser.Name;
    inspection.Description_InspectionStatus = r.InspectionStatus.Description;
    inspection.OccurenceDate_InspectorTracking = r.InspHasWorkitem ? reduceInspTrackDate(r.InspHasWorkitem) : null;
    inspection.Name_ManagerAssignment_Rassignment = (r.Rassignment && r.Rassignment.ManagerAssignment) ? r.Rassignment.ManagerAssignment.Name : null;
    inspection.Comments = (r.QcComments && r.QcComments.length > 0) ? true : false;
    return inspection;
  }
  );
  return {
    data, count
  };

};

function convert_inspections_view_to_table_schema({ "value": items, "@odata.count": count }) {
    const data = items.map(r => {
        const inspection = new InspectionView(r);
        inspection.Name_SystemUser_Rassignment = r.Resource;
        inspection.Name_SystemUser_Qcresource = r.QCName;
        inspection.Description_InspectionStatus = r.Status;
        inspection.OccurenceDate_InspectorTracking = r.OccurenceDate;
        inspection.Name_ManagerAssignment_Rassignment = r.Manager;
        inspection.Comments = (r.QcComments && r.QcComments.length > 0) ? true : false;
        inspection.InspectionId = r.InspectionID;
        return inspection;
    }
    );
    return {
        data, count
    };

};

async function getInspection(inspNum) {
  const filter = { "InspectionNumber": inspNum };
    const params = buildQuery({ filter, expand: Inspection.expand });
    const data = await ApiRequest(endpointInspection, params);
  const item = data.value[0];
  const inspection = new Inspection(item);

  inspection.Name_SystemUser_Rassignment = item.Rassignment && item.Rassignment.SystemUser.Name;
  inspection.Name_SystemUser_Qcresource = item.Qcresource && item.Qcresource.SystemUser.Name;
    inspection.Description_InspectionStatus = item.InspectionStatus.Description;
    inspection.OccurenceDate_InspectorTracking = item.InspHasWorkitem ? reduceInspTrackDate(item.InspHasWorkitem) : null;
  inspection.Name_ManagerAssignment_Rassignment = item.ManagerAssignment ? item.ManagerAssignment.Name : null;
  return inspection;
}

async function getProjectInspections(page, per_page, orderByInfo, orderByDirection, search, filters, exportFilter = false) {
    console.log(exportFilter)
    let { skip, count, top, expand, orderBy, mappedFilter } = ODataUtil.UtilityBuilderNestedv3(
        per_page, page, InspectionView.expand, orderByInfo, orderByDirection, filters
    );
    var result = filters.filter(obj => {
        return obj.column && obj.column.field === "Name_SystemUser_Rassignment"
    })

    var updateFilter = result.length > 0 && mappedFilter.filter(obj => {
        return obj && obj["Qcresource/SystemUser/Name"] && delete Object.assign(obj, { ["Rassignment/SystemUser/Name"]: obj["Qcresource/SystemUser/Name"] })["Qcresource/SystemUser/Name"];
    })

    var comments = filters.filter(obj => {
        return obj.column && obj.column.field === "Comments"
    })

    var updateFilter = comments.length > 0 && mappedFilter.filter(obj => {
        obj && obj["Comments"] && delete Object.assign(obj, { ["QcComments"]: obj["Comments"] })["Comments"];
        if (obj && obj["QcComments"] && !obj["QcComments"]["eq"]) obj["QcComments"]["eq"] = null;
        else if (obj && obj["QcComments"] && obj["QcComments"]["eq"]) { delete obj["QcComments"]["eq"]; obj["QcComments"]["ne"] = null; }
    })

    if (orderByInfo && orderByInfo.field && orderByInfo.field === "Name_SystemUser_Rassignment") orderBy = "Rassignment/SystemUser/Name " + orderByDirection
    if (orderByInfo && orderByInfo.field && orderByInfo.field === "OccurenceDate_InspectorTracking") orderBy = "OccurenceDate " + orderByDirection
    const filter = {};
    const isDefaults = filters.find(r => r.defaults);

    if (isDefaults) {
        const defaults = isDefaults.defaults;
        if (defaults.projectId) filter["ProjectId"] = { eq: { type: 'guid', value: defaults.projectId } };
        if (defaults.inspectionNumber) filter["InspectionNumber"] = defaults.inspectionNumber;
    };

    // Remove the InspHasWorkitem/InspectorTracking/OccurenceDate filter from mappedFilter
    const index = mappedFilter.findIndex(f => f && f.hasOwnProperty("InspHasWorkitem/InspectorTracking/OccurenceDate"));
    if (index !== -1) {
        mappedFilter.splice(index, 1);
    }

    const endpointCall = exportFilter ? endpointExport : endpoint;

    // Build the query without the InspHasWorkitem/InspectorTracking/OccurenceDate filter
    const allFilters = mappedFilter.reduce((a, b) => ({ ...a, ...b }), filter);

    // Only request $count when we don't already have a total for this exact filter set.
    // The count is independent of top/skip/orderBy, so paging and sorting reuse the cached
    // value and skip the expensive COUNT(*) over the view.
    const countKey = JSON.stringify({ endpointCall, filter: allFilters });
    const haveCount = _inspectionCountCache.has(countKey);

    const params = buildQuery({ count: haveCount ? undefined : count, top, skip, orderBy, expand, filter: allFilters });
    const data = await ApiRequest(endpointCall, params);

    if (haveCount) {
        data["@odata.count"] = _inspectionCountCache.get(countKey);
    } else if (data["@odata.count"] !== undefined) {
        _inspectionCountCache.set(countKey, data["@odata.count"]);
    }


    // Client-side filtering for OccurenceDate_InspectorTracking
    const occurenceDateFilter = filters.find(f => f.column && f.column.field === "OccurenceDate_InspectorTracking");
    if (occurenceDateFilter) {
        data.value = data.value.filter(a => {
            const trackAItem = a.InspHasWorkitem && a.InspHasWorkitem.find(item => item.InspectorTracking && item.InspectorTracking.length > 0);
            if (trackAItem) {
                const dateA = new Date(trackAItem.InspectorTracking[0].OccurenceDate);
                return dateA >= occurenceDateFilter.value.ge && dateA <= occurenceDateFilter.value.le;
            }
            return false;
        });
    }

    // Client-side sorting for OccurenceDate_InspectorTracking
    if (orderByInfo && orderByInfo.field && orderByInfo.field === "OccurenceDate_InspectorTracking") {
        data.value.sort((a, b) => {
            const trackAItem = a.InspHasWorkitem && a.InspHasWorkitem.find(item => item.InspectorTracking && item.InspectorTracking.length > 0);
            const trackBItem = b.InspHasWorkitem && b.InspHasWorkitem.find(item => item.InspectorTracking && item.InspectorTracking.length > 0);
            const dateA = trackAItem ? new Date(trackAItem.InspectorTracking[0].OccurenceDate) : new Date(0);
            const dateB = trackBItem ? new Date(trackBItem.InspectorTracking[0].OccurenceDate) : new Date(0);
            return orderByDirection === "asc" ? dateA - dateB : dateB - dateA;
        });
    }

    return convert_inspections_view_to_table_schema(data);
}


async function getInspectionReports(inspId) {
  const endpoint = "/Inspection/Reports";
  const query = `?inspectionId=${inspId}`;
  const req = await ApiRequest(endpoint, query);
  return req.data.map(r => new InspectionReport(r));
};

async function updateInspection(inspectionId, data) {
  const endpoint = "/Inspection/Update";

  const params = `?inspectionid=${inspectionId}&comments=${data.comments}`;

  const result = await patchItem(endpoint, params, data);

  return result;
}

// because this is some more complex business logic,
// it's wrapped up in a class of functions that can be referenced in many components;
class InspectionAndFieldTestActions {

  static inspectionActions = this.freeze({
    approve: "Complete",
    deny: "Rejected"
  });

  static fieldTestActions = this.freeze({
    'true': true,
    'false': false
  });

  static freeze(e) { return Object.freeze(e) };

  static determineInspectionActions(inspectionStatus) {
    switch (inspectionStatus) {
      case 'Active':
        return [];
      case 'Rejected':
        return [];
      case 'Complete':
        return [this.inspectionActions.deny];
      case 'Client Signed':
        return [];
      case 'Submitted for QC':
        return [this.inspectionActions.approve, this.inspectionActions.deny];
      default:
        throw Error('unexpected inspection status retrieved, please review database table and update this function with a new case');
    };
  };

  static canReviewFT(fieldTest) {
    const { Description_TestResults, Description_ReviewStatus } = fieldTest;
    const status = Description_TestResults;
    const approvedWEnj = Description_ReviewStatus;
    return status === "Fail" && !approvedWEnj;
  };

  // wow thats a long name
  static canApproveInspBasedOnFieldTests(fieldTests) {
    return fieldTests.filter(r =>
      InspectionAndFieldTestActions.canReviewFT(r)
    ).length === 0
  };


  static determineActions(inspectionStatus, fieldTests) {
    const inspActions = this.determineInspectionActions(inspectionStatus);
    const fieldTestsOK = this.canApproveInspBasedOnFieldTests(fieldTests);
    if (!fieldTestsOK) {
      return inspActions.filter(r => r !== this.inspectionActions.approve);
    } else {
      return inspActions;
    }
  };

  static canReviewInspection(inspectionStatus) {
    const actions = this.determineInspectionActions(inspectionStatus);
    return actions.length > 0;
  };



};


/**
 * Fetch Additional Fields configuration for a specific table/module
 * @param {string} projectId - The project ID
 * @param {string} tableName - The table/module name (e.g., "INSPECTION", "FIELD_TEST", etc.)
 * @returns {Promise<Array>} Array of field configurations with show/hide status
 */
async function getAdditionalFieldsForTable(projectId, tableName) {
  const params = `?projectId=${projectId}`;
  const data = await ApiRequest("/IrConfig/AdditionalFields", params);

  // Filter for the specific table and return only fields that are shown
  if (data && data.data) {
    return data.data
      .filter(field => field.tableName === tableName)
      .reduce((acc, field) => {
        acc[field.fieldName] = field.show;
        return acc;
      }, {});
  }

  return {};
}

export { getProjectInspections, getInspection, getInspectionReports, InspectionAndFieldTestActions, updateInspection, getAdditionalFieldsForTable };